// ============================================================================
// courseColors.js  —  Mappa colori per tipologia di corso (v1.7.0)
// ----------------------------------------------------------------------------
// Fornisce:
//   - getCourseColor(nomeCorso)     -> colore HEX di sfondo per il corso
//   - getContrastText(hexColor)     -> '#ffffff' o '#000000' per leggibilita'
//   - getCourseColorStyle(nome)     -> { backgroundColor, color } pronto all'uso
//   - COURSE_COLOR_LEGEND           -> array {label, color} per legende UI
//
// La corrispondenza avviene tramite REGOLE ORDINATE (le piu' specifiche prima):
// per ogni corso si scorre la lista di regole e si applica la prima che matcha
// (case-insensitive, per sottostringa). In assenza di match si usa il colore
// di default.
// ============================================================================

// Colore di default (nessuna categoria riconosciuta) — blu neutro storico.
export const DEFAULT_COURSE_COLOR = '#3174AD';

// Regole ordinate: la PRIMA regola che matcha vince. Mettere sempre le varianti
// piu' specifiche (es. "Antincendio Avanzato") PRIMA di quelle generiche
// (es. "Antincendio").
const COLOR_RULES = [
  // ---- ANTINCENDIO -----------------------------------------------------------
  { test: /antincendio avanzato|advanced fire/i, color: '#e74c3c' },   // Rosso
  { test: /antincendio base/i,                    color: '#e67e22' },   // Arancione
  { test: /antincendio/i,                         color: '#e67e22' },   // Arancione (fallback)

  // ---- SOPRAVVIVENZA & SALVATAGGIO ------------------------------------------
  { test: /sopravvivenza|salvataggio|survival craft|rescue boat/i, color: '#2ecc71' }, // Verde

  // ---- PRIMO SOCCORSO / MEDICAL ---------------------------------------------
  { test: /primo soccorso elementare/i,           color: '#ff6b6b' },   // Rosso chiaro
  { test: /primo soccorso sanitario/i,            color: '#fd79a8' },   // Rosa
  { test: /medical care/i,                        color: '#e84393' },   // Magenta
  { test: /medical first aid/i,                   color: '#e84393' },   // Magenta
  { test: /medical|soccorso/i,                    color: '#fd79a8' },   // Rosa (fallback medico)

  // ---- PSSR -----------------------------------------------------------------
  { test: /pssr/i,                                color: '#95a5a6' },   // Grigio

  // ---- LEADERSHIP -----------------------------------------------------------
  { test: /leadership/i,                          color: '#3498db' },   // Blu

  // ---- NAVIGAZIONE / PONTE (toni blu) ---------------------------------------
  { test: /radar arpa/i,                          color: '#1f6391' },   // Blu scuro
  { test: /radar navigation/i,                    color: '#2980b9' },   // Blu
  { test: /radar/i,                               color: '#5dade2' },   // Blu chiaro
  { test: /ecdis/i,                               color: '#2980b9' },   // Blu
  { test: /gmdss/i,                               color: '#3498db' },   // Blu
  { test: /bridge resource|^brm|brm-p/i,          color: '#5dade2' },   // Blu chiaro
  { test: /engine room resource|errm/i,           color: '#48c9b0' },   // Turchese

  // ---- BST (Basic Safety Training) ------------------------------------------
  { test: /basic safety|corso bst|refresh bst|^bst|bst x|bst \+/i, color: '#16a085' }, // Verde acqua

  // ---- SECURITY -------------------------------------------------------------
  { test: /ship security|sso|pfso/i,              color: '#34495e' },   // Blu ardesia
  { test: /security awar|security dut|security/i, color: '#34495e' },   // Blu ardesia

  // ---- CROWD & CRISIS -------------------------------------------------------
  { test: /crowd\s*&?\s*crisis/i,                 color: '#9b59b6' },   // Viola

  // ---- MABEV / MAMS ---------------------------------------------------------
  { test: /mabev/i,                               color: '#8e44ad' },   // Viola scuro
  { test: /mams/i,                                color: '#a569bd' },   // Viola chiaro

  // ---- DYNAMIC POSITIONING (DP) ---------------------------------------------
  { test: /\bdp\b|dynamic positioning|dp awar|dp induction|dp mainten|dp reval|dp simul/i, color: '#0984e3' }, // Blu vivo

  // ---- TANKER / CHIMICA / GAS / PETROLIO ------------------------------------
  { test: /chimichiere|gasiere|petroliere|petrol|chimica|sicurezza gas/i, color: '#b9770e' }, // Ambra scuro

  // ---- POLAR CODE -----------------------------------------------------------
  { test: /polar code/i,                          color: '#74b9ff' },   // Azzurro ghiaccio

  // ---- IGF ------------------------------------------------------------------
  { test: /igf/i,                                 color: '#6c5ce7' },   // Indaco

  // ---- HIGH VOLTAGE ---------------------------------------------------------
  { test: /high voltage|alta tensione/i,          color: '#f1c40f' },   // Giallo

  // ---- STCW -----------------------------------------------------------------
  { test: /stcw/i,                                color: '#2c3e50' },   // Navy

  // ---- FORMAZIONE / DIRETTIVO / MOTORISTI / VARIE ---------------------------
  { test: /formatori|formazione per formatori/i,  color: '#7f8c8d' },   // Grigio scuro
  { test: /direttivo/i,                           color: '#616a6b' },   // Grigio ardesia
  { test: /motoristi/i,                           color: '#d35400' },   // Arancio scuro
  { test: /haccp/i,                               color: '#27ae60' },   // Verde alimentare
  { test: /hvac/i,                                color: '#00cec9' },   // Ciano
  { test: /h&s|health.*safety|h&s di bordo/i,     color: '#1abc9c' },   // Verde acqua
  { test: /diporto/i,                             color: '#0abde3' },   // Azzurro
];

// Cache per non ricalcolare ripetutamente lo stesso nome.
const _colorCache = {};

/**
 * Ritorna il colore HEX associato al nome/tipo di corso.
 * @param {string} courseName - es. "Antincendio Base", "PSSR", "Radar ARPA"
 * @returns {string} colore HEX (es. "#e67e22")
 */
export function getCourseColor(courseName) {
  if (!courseName || typeof courseName !== 'string') return DEFAULT_COURSE_COLOR;
  const key = courseName.trim();
  if (_colorCache[key] !== undefined) return _colorCache[key];

  let result = DEFAULT_COURSE_COLOR;
  for (const rule of COLOR_RULES) {
    if (rule.test.test(key)) {
      result = rule.color;
      break;
    }
  }
  _colorCache[key] = result;
  return result;
}

/**
 * Calcola se su un dato colore di sfondo il testo debba essere bianco o nero,
 * usando la luminanza relativa (WCAG). Ritorna '#000000' su sfondi chiari,
 * '#ffffff' su sfondi scuri.
 * @param {string} hexColor - colore HEX (#rgb o #rrggbb)
 * @returns {string} '#000000' | '#ffffff'
 */
export function getContrastText(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') return '#ffffff';
  let hex = hexColor.replace('#', '').trim();
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  if (hex.length !== 6) return '#ffffff';
  const r = parseInt(hex.slice(0, 2), 16) / 255;
  const g = parseInt(hex.slice(2, 4), 16) / 255;
  const b = parseInt(hex.slice(4, 6), 16) / 255;
  // Luminanza relativa (formula WCAG semplificata)
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Helper che ritorna direttamente lo stile { backgroundColor, color } con testo
 * a contrasto corretto per un dato corso.
 * @param {string} courseName
 * @param {string} [overrideColor] - se presente (es. colore da DB) ha priorita'
 * @returns {{backgroundColor: string, color: string}}
 */
export function getCourseColorStyle(courseName, overrideColor) {
  const bg = (overrideColor && String(overrideColor).trim())
    ? overrideColor
    : getCourseColor(courseName);
  return { backgroundColor: bg, color: getContrastText(bg) };
}

/**
 * Legenda sintetica delle categorie principali per la UI.
 */
export const COURSE_COLOR_LEGEND = [
  { label: 'Antincendio Base',            color: '#e67e22' },
  { label: 'Antincendio Avanzato',        color: '#e74c3c' },
  { label: 'Sopravvivenza & Salvataggio', color: '#2ecc71' },
  { label: 'Primo Soccorso Elementare',   color: '#ff6b6b' },
  { label: 'Primo Soccorso / Medical',    color: '#fd79a8' },
  { label: 'PSSR',                        color: '#95a5a6' },
  { label: 'Leadership',                  color: '#3498db' },
  { label: 'Navigazione (Radar/ECDIS)',   color: '#2980b9' },
  { label: 'BST',                         color: '#16a085' },
  { label: 'Security / SSO / PFSO',       color: '#34495e' },
  { label: 'Crowd & Crisis',              color: '#9b59b6' },
  { label: 'MABEV / MAMS',                color: '#8e44ad' },
  { label: 'Dynamic Positioning (DP)',    color: '#0984e3' },
  { label: 'Tanker / Gas / Petrolio',     color: '#b9770e' },
  { label: 'Polar Code',                  color: '#74b9ff' },
  { label: 'IGF',                         color: '#6c5ce7' },
  { label: 'High Voltage',                color: '#f1c40f' },
  { label: 'STCW',                        color: '#2c3e50' },
  { label: 'Altro',                       color: DEFAULT_COURSE_COLOR },
];

export default getCourseColor;
